/**
 * MUI Theme — espelha tokens IAT (paleta da Beny).
 * Cria um tema por effectiveTheme ('light' | 'dark') e consome cores via CSS vars
 * para que mudanças de tema fiquem instantâneas (sem reconciliação MUI).
 */
import { createTheme } from '@mui/material/styles';

const IAT = {
  yellow:      '#FFC107',
  yellowDeep:  '#F59E0B',
  yellowDark:  '#B7791F',
  yellowSoft:  '#FFF8E1',
  graphite:    '#1A1A20',
  charcoal:    '#232329',
  slate:       '#2D2D35',
  steel:       '#3A3A44',
  iron:        '#5A5A65',
  silver:      '#9A9AA5',
  pewter:      '#C4C4CC',
  cloud:       '#E8E8EC',
  mist:        '#F2F2F4',
  paper:       '#FAFAFB',
  white:       '#FFFFFF',
  ink:         '#0E0E12',
  success:     '#10B981',
  warning:     '#F59E0B',
  danger:      '#EF4444',
  info:        '#3B82F6',
};

const fontFamily = "'Inter', -apple-system, system-ui, sans-serif";
const displayFamily = "'Inter Tight', 'Inter', system-ui, sans-serif";

export function buildIATMuiTheme(mode = 'dark') {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: isDark ? IAT.yellow : IAT.yellowDeep,
        light: IAT.yellowSoft,
        dark: IAT.yellowDark,
        contrastText: IAT.ink,
      },
      secondary: {
        main: isDark ? IAT.paper : IAT.ink,
        contrastText: isDark ? IAT.ink : IAT.paper,
      },
      background: {
        default: isDark ? IAT.graphite : IAT.paper,
        paper: isDark ? IAT.charcoal : IAT.white,
      },
      text: {
        primary: isDark ? IAT.paper : IAT.ink,
        secondary: isDark ? IAT.pewter : IAT.iron,
        disabled: isDark ? IAT.iron : IAT.pewter,
      },
      divider: isDark ? IAT.slate : IAT.cloud,
      error:   { main: IAT.danger },
      warning: { main: IAT.warning },
      info:    { main: IAT.info },
      success: { main: IAT.success },
    },
    typography: {
      fontFamily,
      h1: { fontFamily: displayFamily, fontWeight: 800, letterSpacing: '-0.035em' },
      h2: { fontFamily: displayFamily, fontWeight: 800, letterSpacing: '-0.03em' },
      h3: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.025em' },
      h4: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.015em' },
      h6: { fontFamily: displayFamily, fontWeight: 600, letterSpacing: '-0.01em' },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '-0.005em' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, fontWeight: 600 },
          containedPrimary: {
            color: IAT.ink,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? IAT.charcoal : IAT.white,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark ? IAT.charcoal : IAT.white,
          },
          notchedOutline: {
            borderColor: isDark ? IAT.slate : IAT.cloud,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? IAT.slate : IAT.ink,
            color: isDark ? IAT.paper : IAT.paper,
            fontSize: 12,
            borderRadius: 8,
            padding: '6px 10px',
          },
          arrow: { color: isDark ? IAT.slate : IAT.ink },
        },
      },
    },
  });
}
